const API_BASE = import.meta.env.VITE_API_URL || '';

export interface Address {
  streetAddress?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

export interface ProfileStats {
  profileViews: number;
  vcardDownloads: number;
}

export interface Profile {
  publicId: string;
  firstName: string;
  lastName: string;
  title: string;
  tagline?: string;
  phone?: string;
  email?: string;
  website?: string;
  photoUrl?: string;
  accentColor: string;
  address?: Address;
  preferredLanguage?: string;
  createdAt?: string;
  updatedAt?: string;
  stats?: ProfileStats;
}

export interface ContactInfo {
  phone: string;
  email: string;
  vcardToken: string;
  address?: Address;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Public API (no auth required)

export async function getPublicProfile(publicId: string): Promise<Profile> {
  return fetchApi<Profile>(`/api/p/${publicId}`);
}

export async function revealContact(publicId: string): Promise<ContactInfo> {
  return fetchApi<ContactInfo>(`/api/p/${publicId}/reveal`, {
    method: 'POST',
  });
}

export function getVCardUrl(publicId: string, token: string): string {
  return `${API_BASE}/api/vcard/${publicId}?token=${encodeURIComponent(token)}`;
}

// Protected API (auth required)

export async function getOwnProfile(token: string): Promise<Profile> {
  return fetchApi<Profile>('/api/profile', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function createProfile(
  token: string,
  data: Omit<Profile, 'publicId' | 'createdAt' | 'updatedAt'>
): Promise<Profile> {
  return fetchApi<Profile>('/api/profile', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
}

export async function updateProfile(
  token: string,
  data: Partial<Profile>
): Promise<Profile> {
  return fetchApi<Profile>('/api/profile', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
}

export async function deleteProfile(token: string): Promise<void> {
  return fetchApi<void>('/api/profile', {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getPhotoUploadUrl(
  token: string,
  contentType: string
): Promise<{ uploadUrl: string; photoUrl: string }> {
  return fetchApi('/api/profile/photo-url', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ contentType }),
  });
}
